// Taşeron portalı — Belge Analizi gelen kutusu: inceleme paneli (plan S25 C5-C12,
// `docs/plans/S25-analysis-inbox.md`; tasarım incelemesi `docs/reviews/S25-inbox-design.md`).
// `AnalysisReviewPanePlaceholder.tsx`'in YERİNİ alır — o dosyanın kurduğu props sözleşmesi
// (`selection`/`loading`/`headingRef`) burada KORUNUR, yalnız `companyId`/`culture`/`allDecided`/
// `onDecided` eklenir (bkz. `AnalysisReviewPaneProps`).
//
// **Bir belge, bir karar.** Ray'den seçilen TEK kalem burada incelenir; `Onayla` isteği her
// zaman `items: [tekKalem]` taşır (toplu kabul değil) — bu, C10'un onay diyaloğunun "Belge /
// Kişi / Geçerlilik" özetini TEK belge için anlamlı kılar.
//
// **İçerik kalem başına REMOUNT olur** (`key={item.uuid}`, `AnalysisReviewPaneContent`) — form
// taslağı, diyalog durumu, hata bayrakları hepsi bu remount ile SIFIRLANIR; ayrı bir "reset"
// efekti YAZILMAZ.
//
// **Karardan sonra odak (C12/AC16, tasarım D10).** Bu panel "bir sonraki bekleyen belge"yi
// KENDİSİ hesaplamaz (ray'in TÜM ağacına ihtiyaç duyar) — `onDecided(analysisUuid, itemUuid)`
// çağrısıyla üst sayfaya BIRAKIR; sayfa `findNextPendingItem` (lib/analysisInboxTree.ts) ile
// bir sonraki kalemi seçer ve odağı O kalemin panelindeki `h2`'sine taşır — footer'daki
// `Onayla`ya ASLA (D10: aksi hâlde bir klavye kullanıcısı bir sonraki KİŞİNİN belgesini
// onaylamaya bir `Enter` uzaklıkta kalır).
//
// **Reload durability.** Kalemin durumu (`itemStatus`, `lib/analysisInboxTree.ts`) YALNIZ
// `item.acceptedLinkUuid`/`item.rejectedAt`den türetilir — bu bileşen kendi oturum-içi bir
// "sonuç" önbelleği TUTMAZ (S19'un eski `results: Map` deseni buraya TAŞINMAZ); kabul/ret
// sonrası ilgili sorgular geçersiz kılınır, ekran sunucudan gelen TAZE `item`i okur.
//
// **Belge türü (GL #7, §27.7(1), §20 `entity`/`code` notu).** Seçici §27 kaydının hedef entity'sindeki
// AKTİF türlerdir. Tanınan `(entity, code)` yalnız hedefle aynı entity'de ve kayıtta AKTİFSE ön-seçilir;
// pasif/bilinmiyorsa seçici boş kalır ve tanınan etiket türün ipucunda okunur. Bitiş tarihi türün
// `expiryRule`'una göre (`NoExpiry`'de gizli, gönderilmez). Sunucu türü reddederse (`INVALID`/
// `INACTIVE`, §27.6) kayıt tazelenir, seçim temizlenir, hata seçicide, odak oraya — aynı gövde
// yeniden GÖNDERİLMEZ. Kayıt, sayfaya girişte bir kez tazelenir (dış bileşen), kalem başına değil.

import {
  type AcceptDocumentAnalysisRequest,
  type AcceptDocumentAnalysisResultDto,
  type AcceptDocumentAnalysisTarget,
  Alert,
  type ApiError,
  Button,
  ConfirmDialog,
  type DocumentAnalysisItemDto,
  type DocumentAnalysisPageDto,
  type DocumentAnalysisSourceDto,
  Field,
  type HazardClass,
  Input,
  Select,
  type SelectOption,
  Spinner,
  Tag,
  Tooltip,
  documentAnalysesApi,
  isSelectableDocumentType,
  isTypeFieldRejection,
  resolveApiErrorMessage,
  showsValidUntil,
  supplierContractorApi,
  useDocumentTypes,
  useRefetchDocumentTypesOnMount,
  validUntilToSend,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Check, Info, TriangleAlert } from 'lucide-react';
import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  type AnalysisGroup,
  analysisGroupLabel,
  analysisItemLabel,
  itemStatus,
} from '../lib/analysisInboxTree';
import { computeInitialValidity } from '../lib/analysisValidity';
import { formatPageSpan } from '../lib/documentAnalysisPages';
import { formatDate } from '../lib/format';
import { extractedIdentity, personMismatch, personnelRegistrationStatus } from '../lib/personMatch';
import { supplierContractorKeys } from '../lib/supplierContractorKeys';
import { useReturnFocus } from '../lib/useReturnFocus';
import { AnalysisExtractedFields } from './AnalysisExtractedFields';
import { DocumentAnalysisPreview } from './DocumentAnalysisPreview';
import { PersonSelect } from './PersonSelect';
import { PersonnelCreateModal } from './PersonnelCreateModal';
import {
  DocumentTypeSelect,
  DocumentTypesStatus,
  ValidUntilInput,
  documentFieldsComplete,
  useExpiryRule,
} from './documents/DocumentTypeFields';

export interface AnalysisInboxSelection {
  analysisUuid: string;
  item: DocumentAnalysisItemDto;
  group: AnalysisGroup;
  /** `item.sourceDocumentUuid`nün karşılığı — eşlenemezse (nadir, S25 sözleşme notu) `undefined`. */
  source: DocumentAnalysisSourceDto | undefined;
  /** Bu kalemin sayfaları — ÇAĞIRAN (sayfa düzeyi) tarafından ZATEN `pagesForItem`/
   * `groupPagesBySource` ile süzülmüş/sıralanmış (`lib/documentAnalysisPages.ts`). Panel bunu
   * İKİNCİ kez FİLTRELEMEZ (C5). */
  pages: DocumentAnalysisPageDto[];
}

export interface AnalysisReviewPaneProps {
  /** Rayda seçili belge yok — ya henüz hiçbir satır tıklanmadı ya da URL'deki analiz bu
   * oturumun ağacında (henüz) bulunamadı. */
  selection: AnalysisInboxSelection | null;
  /** Seçili kalemin analiz DETAYI hâlâ yükleniyor — `selection === null` ile KARIŞTIRILMAZ. */
  loading: boolean;
  /** Seçili analizin detay sorgusu HATA ile sonuçlandı (F4, S25 güvenlik incelemesi) — ör.
   * firma değiştirildiğinde eski `uuid` artık bu kapsamda çözülmüyor, ya da analiz bu arada
   * silindi. `loading`den AYRI tutulur: sorgu hataya düştüğünde `data` sonsuza kadar
   * `undefined` kalır, bunu `loading` sanmak paneli kalıcı olarak döndürürdü. */
  error?: unknown;
  /** Mount + seçim değişiminde odaklanan başlık — karar-sonrası odak da (C12/D10) BURAYA gelir. */
  headingRef: RefObject<HTMLHeadingElement | null>;
  companyId: number | null;
  culture: string;
  /** `awaiting-review` listesindeki TÜM analizlerde artık bekleyen kalem yok — boş durumun
   * kopyasını seçer ("henüz seçim yok" vs "hepsi karara bağlandı"). */
  allDecided: boolean;
  /** Bir kabul/ret BAŞARIYLA sonuçlandığında (C12/AC16) — bu panel bir SONRAKİ bekleyen belgeyi
   * KENDİSİ hesaplamaz (ray'in tüm ağacına ihtiyaç duyar), çağıran sayfaya bırakır. */
  onDecided: (analysisUuid: string, itemUuid: string) => void;
}

export function AnalysisReviewPane({
  selection,
  loading,
  error,
  headingRef,
  companyId,
  culture,
  allDecided,
  onDecided,
}: AnalysisReviewPaneProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // D9 — kabul formu bir yükleme akışıdır: kayıt sayfaya girişte BİR KEZ tazelenir. İçerik kalem
  // başına remount olduğundan bu çağrı orada değil burada durur (her tıkta bir istek olmasın).
  useRefetchDocumentTypesOnMount();
  // 30 dk'lık presigned URL süresi dolduğunda TEK seferlik yeniden çekiş (§20 DTO notu, C5) —
  // ANALİZ başına. Bu Set panel dış katmanı (bu bileşen) yeniden mount OLMADIĞI sürece kalıcıdır;
  // aynı analizdeki başka bir kaleme geçiş (`AnalysisReviewPaneContent` remount) ikinci bir
  // refetch TETİKLEMEZ — kalıcı biçimde bozuk bir görsel sonsuz döngüye giremez (S24 §C kuralı,
  // analiz kapsamına genişletilmiş).
  const refetchedAnalysesRef = useRef<Set<string>>(new Set());
  function handleThumbnailExpired(analysisUuid: string) {
    if (refetchedAnalysesRef.current.has(analysisUuid)) {
      return;
    }
    refetchedAnalysesRef.current.add(analysisUuid);
    queryClient.refetchQueries({
      queryKey: supplierContractorKeys.analysis(companyId, analysisUuid),
    });
  }

  if (loading) {
    return (
      <div className="flex min-h-[16rem] items-center justify-center border border-border bg-surface p-6">
        <Spinner label={t('analysis.result.loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[16rem] flex-col items-center justify-center gap-1.5 border border-dashed border-border-strong bg-surface p-6 text-center">
        <h2 ref={headingRef} tabIndex={-1} className="text-sm font-medium text-fg outline-none">
          {t('analysis.inbox.pane.errorTitle')}
        </h2>
        <p className="text-sm text-muted">{resolveApiErrorMessage(error, t)}</p>
      </div>
    );
  }

  if (!selection) {
    return (
      <div className="flex min-h-[16rem] flex-col items-center justify-center gap-1.5 border border-dashed border-border-strong bg-surface p-6 text-center">
        <h2 ref={headingRef} tabIndex={-1} className="text-sm font-medium text-fg outline-none">
          {allDecided ? t('analysis.inbox.pane.allDoneTitle') : t('analysis.inbox.pane.emptyTitle')}
        </h2>
        <p className="text-sm text-muted">
          {allDecided ? t('analysis.inbox.pane.allDoneBody') : t('analysis.inbox.pane.emptyBody')}
        </p>
      </div>
    );
  }

  return (
    <AnalysisReviewPaneContent
      key={selection.item.uuid}
      selection={selection}
      headingRef={headingRef}
      companyId={companyId}
      culture={culture}
      onDecided={onDecided}
      onThumbnailExpired={() => handleThumbnailExpired(selection.analysisUuid)}
    />
  );
}

/** Kişi listesi boşken (C7) — boş seçici DEĞİL, açık davet çağrısı (`DocumentAnalysisResultPage.tsx`
 * ile AYNI davranış, o dosyadan import EDİLMEZ — C14 o yüzeyi SİLECEK). */
function PersonnelEmptyState({ loading }: { loading: boolean }) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted">
        <Spinner className="h-4 w-4" />
        {t('common.loading')}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1.5 border border-dashed border-border-strong p-3 text-sm text-muted">
      <p>{t('personnel.empty')}</p>
      <Link to="/personnel" className="w-fit font-medium text-fg underline underline-offset-2">
        {t('personnel.createAction')}
      </Link>
    </div>
  );
}

interface ContentProps {
  selection: AnalysisInboxSelection;
  headingRef: RefObject<HTMLHeadingElement | null>;
  companyId: number | null;
  culture: string;
  onDecided: (analysisUuid: string, itemUuid: string) => void;
  onThumbnailExpired: () => void;
}

function AnalysisReviewPaneContent({
  selection,
  headingRef,
  companyId,
  culture,
  onDecided,
  onThumbnailExpired,
}: ContentProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const registry = useDocumentTypes();
  const { analysisUuid, item, group, source, pages } = selection;
  const status = itemStatus(item);
  const resolved = status !== 'pending';
  // #33 — tanınmayan kalem (`entity === null`, `Belge sahibi belirlenemedi` grubu) firma ya da
  // personel belgesi olabilir; hedef grubundan DEĞİL kullanıcının seçiminden gelir (başta hiçbiri
  // seçili değil). Tanınan kalemlerde davranış aynı: hedef grubundan türer.
  const [chosenTarget, setChosenTarget] = useState<AcceptDocumentAnalysisTarget | ''>('');
  const needsTargetChoice = item.entity === null && group.kind === 'unresolved';
  const isCompanyGroup = needsTargetChoice ? chosenTarget === 'company' : group.kind === 'company';
  const target: AcceptDocumentAnalysisTarget = isCompanyGroup ? 'company' : 'personnel';
  const targetPending = needsTargetChoice && chosenTarget === '';
  // Başlık `(entity, code)`'dan §27 kaydıyla çözülür — rayla AYNI metin (`analysisItemLabel`,
  // `lib/analysisInboxTree.ts`). Sunucu etiket göndermez (§20, #33); kayıtta yoksa ham kod.
  const titleLabel = analysisItemLabel(item, t, registry.label);
  // #33 B.3 — `vehicle` kalemin §20.2'de kabul hedefi YOKTUR (#17'ye kadar): yalnız Reddet.
  const filingSupported = item.entity !== 'vehicle';
  const groupLabel = analysisGroupLabel(group, t);
  const pageSpan = formatPageSpan(item.pages);

  // ---- personel listesi (Kişi seçici + kabul isteğinin `userUuid`i) — Firma belgeleri
  // grubunda hiç SORGULANMAZ (D10: bu grupta Kişi alanı hiç render edilmez). ----------------
  const personnelQuery = useQuery({
    queryKey: supplierContractorKeys.personnel(companyId),
    queryFn: () => supplierContractorApi.listSupplierContractorPersonnel(),
    enabled: companyId != null && !isCompanyGroup && !targetPending,
  });
  const personnelOptions: SelectOption[] = (personnelQuery.data ?? [])
    .filter((p) => p.kind === 'Personnel')
    .map((p) => ({ value: p.uuid, label: `${p.firstName} ${p.lastName}`.trim() }));

  // ---- KAYIT DIŞI (C8/AC7) — GRUBUN TÜMÜ taranır (`kind === 'subject'`de tüm kalemler ZATEN
  // aynı kişiye ait, bkz. `lib/personMatch.ts` dosya üstü notu). Personel listesi henüz
  // yüklenmediyse (`personnelQuery.data === undefined`) ETİKET GÖSTERİLMEZ (yanlış "kayıt dışı"
  // yanıp sönmesin). `personnelRegistrationStatus` TEK giriş noktasıdır (S25 web kod incelemesi
  // #2) — eski `isRegisteredPersonnel` çağrısı SÜZÜLMEMİŞ `personnelQuery.data`yı (aktive
  // personel + bekleyen davetler BİRLİKTE) taşıyordu; bir davet satırı ad-yedek dalıyla
  // eşleşince kişi sessizce "kayıtlı" sayılıyor, hem rozet hem "Yeni kişi ekle" kaçış yolu
  // BASTIRILIYORDU. ------------------------------------------------------------------------------
  const groupIdentity = extractedIdentity(group.subject, group.items);
  const registrationStatus =
    group.kind === 'subject' && personnelQuery.data !== undefined
      ? personnelRegistrationStatus(groupIdentity, personnelQuery.data)
      : null;
  // Kişi seçicinin/boş-durumun/kaçış yolunun kapısı — "hiç duyulmamış biri" (`unregistered`) VE
  // "davet edildi ama henüz etkinleşmedi" (`invited`) AYNI şekilde ele alınır: ikisi de aktive
  // personel listesinde YOK, ikisi de bugün belge alamaz. Rozet metni/tonu AŞAĞIDA ayrışır —
  // kullanıcı "bu kişiyi hiç duymadım" ile "bu kişiyi zaten davet ettim, bekliyor"u KARIŞTIRMASIN.
  const unregisteredSubject = registrationStatus !== null && registrationStatus !== 'registered';
  const invitedSubject = registrationStatus === 'invited';

  // ---- Uyuşmazlık uyarısı (C8/AC8) — yalnız BU kalemin kendi alanları taranır (`Kişi
  // belirlenemedi` grubunda `group.items` FARKLI kişilere ait olabilir). Onayı ASLA ENGELLEMEZ.
  const itemIdentity = extractedIdentity(group.subject, [item]);

  // ---- taşeronun tehlike sınıfı — İSG eğitim geçerliliğinin üç adayından hangisinin
  // ön-doldurulacağını belirler (`computeInitialValidity`, `DocumentAnalysisItemCard.tsx`). ----
  const contractorQuery = useQuery({
    queryKey: supplierContractorKeys.contractor(companyId),
    queryFn: () => supplierContractorApi.getSupplierContractor(),
    enabled: companyId != null,
  });
  const hazardClass: HazardClass | null = contractorQuery.data?.hazardClass ?? null;
  const validity = computeInitialValidity(item, hazardClass);

  const [personUuid, setPersonUuid] = useState('');
  // F6 — tanınan kalem yalnız varlığı GRUBUN hedefiyle (`target`) AYNIYSA VE kod o hedefte kayıtta
  // AKTİFSE ön-seçilir; aksi hâlde boş kalır, kullanıcı KENDİSİ seçer (§20 `entity`/`code` notu).
  // `null` = kullanıcı henüz seçmedi → tanınan varsayılan; varsayılan TÜRETİLİR (kayıt mount'tan
  // sonra gelebilir, ön-seçim ilk render'a bağlı kalmasın).
  const [docTypeChoice, setDocTypeChoice] = useState<string | null>(null);
  const recognisedCode = item.entity !== null && item.entity === target ? item.code : null;
  const recognisedSelectable =
    recognisedCode !== null && isSelectableDocumentType(registry.types, target, recognisedCode);
  const docTypeValue = docTypeChoice ?? (recognisedSelectable ? recognisedCode : '');
  // Sunucu bu türü reddetti (§27.6) — hata seçicide; kullanıcı türü değiştirince düşer.
  const [typeRejected, setTypeRejected] = useState(false);
  const typeSelectRef = useRef<HTMLSelectElement>(null);
  const expiryRule = useExpiryRule(target, docTypeValue);
  const [validFrom, setValidFrom] = useState(validity.validFrom);
  const [validUntil, setValidUntil] = useState(validity.validUntil);
  // `Not` YALNIZ insan yazar — model çıktısından ÖN-DOLDURULMAZ (C7, paylaşılan DTO kuralı).
  const [note, setNote] = useState('');
  // Kullanıcı `Geçerlilik bitişi`ni KENDİSİ değiştirdiyse aşağıdaki geri-doldurma efekti bir
  // daha ÜZERİNE YAZMAZ (FIX 5 emsali, `DocumentAnalysisItemCard.tsx`).
  const validUntilTouchedRef = useRef(false);
  const sentValidUntil = validUntilToSend(registry.types, target, {
    type: docTypeValue,
    validUntil,
  });

  // `hazardClass` sorgusu bu bileşen mount olduktan SONRA gelirse bitiş tarihini BİR KEZ geri
  // doldurur — ilk `useState` başlatıcısı `hazardClass === null` iken çalışmış olabilir.
  useEffect(() => {
    if (validUntilTouchedRef.current) {
      return;
    }
    const recomputed = computeInitialValidity(item, hazardClass);
    if (recomputed.validUntil.length > 0) {
      setValidUntil((prev) => (prev.length > 0 ? prev : recomputed.validUntil));
    }
    // `item` bu bileşenin `key`iyle SABİT (remount olmadan değişmez) — burada listelenmesi
    // yalnız bir sunucu yeniden-çekişinin `item` referansını tazelediği (aynı `uuid`) nadir
    // anda etkiye sahiptir, o an da bu efeğin zaten yaptığı şeyi (bir kez, dokunulmamışsa
    // doldur) yeniden yapmak zararsızdır.
  }, [hazardClass, item]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  // `DOCUMENT_ANALYSIS_ARTIFACT_MISSING` KALICIDIR — yeniden dene afordansı hiç GÖSTERİLMEZ
  // (§20.2); bu bayrak `Onayla`yı kalıcı olarak kilitler (`Reddet` hâlâ mümkündür).
  const [artifactMissing, setArtifactMissing] = useState(false);
  const [personNotFoundError, setPersonNotFoundError] = useState(false);
  // `Modal`ın `Dialog.Trigger`ı YOK (bkz. `useReturnFocus.ts` dosya üstü notu) — normalde
  // diyalog KAPANDIĞINDA odak `Onayla`ya döner (Vazgeç/Esc/dış tık). Kabul BAŞARILI olduğunda
  // bu KASITLI OLARAK atlanır (D10) — `justDecidedRef` bu iki yolu ayırır.
  const justDecidedRef = useRef(false);
  const confirmTrigger = useReturnFocus<HTMLButtonElement>();
  const personTriggerRef = useRef<HTMLButtonElement>(null);

  // C9 — personel oluşturma modalı. Tetikleyicisi `PersonSelect` içindeki sentinel satır
  // (`Select.Item`, ephemeral/portallı) — `useReturnFocus`in sabit-ref modu buraya UYMAZ. Radix
  // `Select` (Modal'ın AKSİNE) kendi `Trigger`ını taşır: popover sentinel seçimiyle kapandığında
  // odağı KENDİLİĞİNDEN `personTriggerRef`e (Select.Trigger) döndürür — bu yüzden modalın
  // kapanışında yalnız AYNI `personTriggerRef`e geri dönmek yeterli (`justCreatedRef`
  // `justDecidedRef`in deseniyle AYNI: İPTAL/Esc/dış tıkta döner, BAŞARILI oluşturmada bunu
  // KASITLI OLARAK atlar — orada zaten yeni kişi seçilip AYNI ref'e odaklanılır).
  const [createOpen, setCreateOpen] = useState(false);
  const justCreatedRef = useRef(false);

  const selectedPerson =
    (personnelQuery.data ?? []).find((p) => p.kind === 'Personnel' && p.uuid === personUuid) ??
    null;
  const mismatch = selectedPerson ? personMismatch(itemIdentity, selectedPerson) : null;

  const derived = item.derived.isgEgitimValidUntil;
  const derivedSourceOk = derived !== null && item.fields[derived.sourceField]?.parse === 'ok';

  function invalidateAfterDecision(acceptedPersonUuid: string | null) {
    // Reddet akışının (eski `DocumentAnalysisResultPage.tsx`) izlediği AYNI dört aile + kabulün
    // kendi üç ailesi (§20.2 sıralama kısıtı) — birleşik süperkümesi: belgeler, uygunluk özeti,
    // (kabul edilen kişi varsa) o kişinin belgeleri, DÖRT liste süzgeci VE analiz detayının
    // kendisi. Detay geçersiz kılınmadan ray'in ✓ işareti ve `X/Y karar verildi` sayacı
    // TAZELENMEZ (reload-durability'nin oturum-İÇİ karşılığı).
    queryClient.invalidateQueries({ queryKey: supplierContractorKeys.documents(companyId) });
    queryClient.invalidateQueries({ queryKey: supplierContractorKeys.contractor(companyId) });
    if (acceptedPersonUuid) {
      queryClient.invalidateQueries({
        queryKey: supplierContractorKeys.personnelDocuments(companyId, acceptedPersonUuid),
      });
    }
    for (const filter of ['awaiting-review', 'active', 'all', 'archived'] as const) {
      queryClient.invalidateQueries({
        queryKey: supplierContractorKeys.analyses(companyId, filter),
      });
    }
    queryClient.invalidateQueries({
      queryKey: supplierContractorKeys.analysis(companyId, analysisUuid),
    });
  }

  const acceptMutation = useMutation<AcceptDocumentAnalysisResultDto, ApiError, void>({
    mutationFn: () => {
      const base = {
        itemUuid: item.uuid,
        validFrom: validFrom.length > 0 ? validFrom : null,
        // Türün kuralı tarihi gizliyorsa (`NoExpiry`) ASLA gönderilmez (D8).
        validUntil: sentValidUntil,
        note: note.trim().length > 0 ? note.trim() : null,
      };
      // §20.2: `validFrom`/`validUntil` `DateTime`dir (UTC ISO 8601) — bir gün değeri
      // (`yyyy-MM-dd`, `<input type="date">`'in kendi biçimi) sunucuda günün başına bağlanır;
      // istemci burada AYRICA bir ISO dönüşümü YAPMAZ (sözleşme bunu açıkça izin verir).
      const request: AcceptDocumentAnalysisRequest = isCompanyGroup
        ? { items: [{ ...base, target: 'company', type: docTypeValue }] }
        : { items: [{ ...base, target: 'personnel', type: docTypeValue, userUuid: personUuid }] };
      return documentAnalysesApi.acceptDocumentAnalysis(analysisUuid, request);
    },
    onError: (error) => {
      // §27.6 — tür reddi: kayıt tazelenir, seçim temizlenir, hata seçicide, odak oraya; diyalog
      // kapanır (PERSONNEL_NOT_FOUND ile AYNI desen) — aynı gövde yeniden GÖNDERİLMEZ. Başka her
      // hata diyalogda kalır (`ConfirmDialog` `error`).
      if (!isTypeFieldRejection(error)) {
        return;
      }
      void registry.refetch();
      setDocTypeChoice('');
      setTypeRejected(true);
      setConfirmOpen(false);
      requestAnimationFrame(() => typeSelectRef.current?.focus());
    },
    onSuccess: (response) => {
      // Kısmi başarı VERİDİR, zarf hatası DEĞİLDİR — HER ZAMAN `results[].outcome`e göre
      // dallanılır, `isSuccess`e (HTTP `200`) göre ASLA (bu istek tek kalemlik olsa bile).
      const result = response.results[0];
      if (!result) {
        return;
      }
      if (result.outcome === 'accepted' || result.outcome === 'already-accepted') {
        justDecidedRef.current = true;
        setConfirmOpen(false);
        invalidateAfterDecision(isCompanyGroup ? null : personUuid);
        onDecided(analysisUuid, item.uuid);
        return;
      }
      // outcome === 'failed' — kapalı küme, tam olarak ÜÇ değer (§20.2).
      if (result.errorCode === 'PERSONNEL_NOT_FOUND') {
        // Seçilen kişi artık çözülemiyor (silindi/başka taşeron) — Kişi seçici YENİDEN açılır.
        setConfirmOpen(false);
        setPersonUuid('');
        setPersonNotFoundError(true);
        requestAnimationFrame(() => personTriggerRef.current?.focus());
        return;
      }
      if (result.errorCode === 'DOCUMENT_ANALYSIS_ARTIFACT_MISSING') {
        // Kalıcı — form tüm işlevini kaybeder (statik `Alert`e döner), diyaloğun kendi
        // tetikleyicisi (`Onayla`) `canSubmit` ile kilitlenip artık odak ALAMAZ; odağı en
        // yakın anlamlı yer olan başlığa taşı (mount-focus deyimiyle AYNI hedef).
        setConfirmOpen(false);
        setArtifactMissing(true);
        requestAnimationFrame(() => headingRef.current?.focus());
        return;
      }
      // DOCUMENT_ANALYSIS_ARTIFACT_COPY_FAILED — GEÇİCİDİR, yeniden denemek anlamlıdır: diyalog
      // AÇIK kalır, `Onayla` (aşağıdaki `copyFailed` uyarısıyla) yeniden basılabilir.
    },
  });

  const rejectMutation = useMutation<
    Awaited<ReturnType<typeof documentAnalysesApi.rejectDocumentAnalysisItem>>,
    ApiError,
    void
  >({
    mutationFn: () => documentAnalysesApi.rejectDocumentAnalysisItem(analysisUuid, item.uuid),
    onSuccess: () => {
      invalidateAfterDecision(null);
      onDecided(analysisUuid, item.uuid);
    },
  });

  const lastAcceptResult = acceptMutation.data?.results[0];
  const copyFailed =
    lastAcceptResult?.outcome === 'failed' &&
    lastAcceptResult.errorCode === 'DOCUMENT_ANALYSIS_ARTIFACT_COPY_FAILED';

  const personRequired = !isCompanyGroup;
  const canSubmit =
    filingSupported &&
    !targetPending &&
    !artifactMissing &&
    // F6 — tür hedefin entity'sinde kayıtta AKTİF olmalı (seçici yalnız bunları sunar; bu ikinci
    // kapı savunmadır — `Onayla` asla kullanıcının GÖRMEDİĞİ bir türle etkinleşmez) ve kural
    // istiyorsa (`EndDateRequired`) bitiş tarihi dolu olmalı.
    documentFieldsComplete(registry.types, target, { type: docTypeValue, validUntil }) &&
    (!personRequired || personUuid.length > 0) &&
    !acceptMutation.isPending &&
    !rejectMutation.isPending;

  const selectedPersonLabel = personnelOptions.find((o) => o.value === personUuid)?.label ?? '—';
  // Özet GÖNDERİLECEK değeri okur: gizli (`NoExpiry`) bir tarih burada da görünmez.
  const validitySummary =
    validFrom.length === 0 && sentValidUntil === null
      ? t('analysis.review.confirm.validityEmpty')
      : `${validFrom ? formatDate(validFrom, culture) : '—'} –\u00A0${
          sentValidUntil ? formatDate(sentValidUntil, culture) : '—'
        }`;
  const chosenTypeLabel = docTypeValue ? registry.label(target, docTypeValue) : '—';
  // Tanınan tür bu hedefte artık kullanılamıyorsa (pasif/kayıtta yok) seçici boştur; etiketi ipucunda.
  const recognisedInactiveHint =
    docTypeValue === '' &&
    recognisedCode !== null &&
    registry.inactiveOrUnknown(target, recognisedCode)
      ? t('analysis.review.recognisedInactive', { label: registry.label(target, recognisedCode) })
      : undefined;

  function changeDocType(code: string) {
    setDocTypeChoice(code);
    setTypeRejected(false);
    const next = registry.find(target, code);
    if (!showsValidUntil(next)) {
      // D8 — gizlenen tarih sessizce taşınmaz.
      setValidUntil('');
    } else if (validUntil.length === 0 && !validUntilTouchedRef.current) {
      // Tarihi yeniden gösteren bir türe dönüldü: kullanıcı dokunmadıysa hesaplanan tarih geri gelir
      // (aksi hâlde "hesaplandı" ipucu boş bir alanın altında yalan söylerdi).
      setValidUntil(computeInitialValidity(item, hazardClass).validUntil);
    }
  }

  const typeField = (
    <DocumentTypeSelect
      id="review-doc-type"
      label={t('documents.field.type')}
      entity={target}
      value={docTypeValue}
      disabled={artifactMissing}
      error={typeRejected ? t('documentTypes.typeRejected') : undefined}
      hint={recognisedInactiveHint}
      selectRef={typeSelectRef}
      onChange={changeDocType}
    />
  );

  function openConfirm() {
    setPersonNotFoundError(false);
    setConfirmOpen(true);
  }

  return (
    <div className="flex flex-col bg-surface">
      {/* `@container` (Tailwind v4 core, `container-type: inline-size`) — bu panelin iki
          sütuna ne zaman geçeceği VIEWPORT genişliğine değil, bu kartın kendi genişliğine
          bağlıdır (tasarım incelemesi sign-off §3.1, seçenek A). Sidebar/rail/sayfa dolgusu
          gibi ATALARIN genişliği değişse bile bu bileşen kendi hesabını YENİDEN YAPMAZ —
          repo'daki İLK `@container` kullanımı, iki-sütunlu her yeni panel için emsal olur. */}
      <div className="@container flex flex-col gap-4 p-4 sm:p-6">
        <div className="flex flex-col gap-1">
          {/* `tabIndex={-1}` — programatik odak hedefi, klavye Tab sırasına GİRMEZ. */}
          <h2 ref={headingRef} tabIndex={-1} className="text-lg font-semibold text-fg outline-none">
            {titleLabel}
          </h2>
          {/* D10 — grup kişi mi firma mı sorusunu ZATEN cevaplar: `Firma belgeleri`/`Kişi
              belirlenemedi` kendi başına açıklayıcıdır, önüne bir "Kişi:" öneki YALNIZ gerçek
              bir kişi adının önünde eklenir. C8/AC7 — KAYIT DIŞI etiketi bu satırda, adın
              YANINDA (rail 2. seviyeyle AYNI yerleşim); kayıtlıysa hiçbir etiket görünmez. */}
          <p className="flex items-center gap-1.5 text-sm text-muted">
            <span>
              {group.kind === 'subject'
                ? `${t('analysis.accept.subjectPicker')}: ${groupLabel}`
                : groupLabel}
            </span>
            {registrationStatus === 'unregistered' ? (
              <Tag color="gray" className="shrink-0 border border-border">
                {t('analysis.person.unregistered')}
              </Tag>
            ) : registrationStatus === 'invited' ? (
              <Tag color="blue" className="shrink-0">
                {t('analysis.person.invited')}
              </Tag>
            ) : null}
          </p>
          {source ? (
            <p className="text-xs text-muted">
              {pageSpan
                ? t('analysis.inbox.itemPageSpan', { fileName: source.fileName, span: pageSpan })
                : source.fileName}
            </p>
          ) : null}
        </div>

        {/* 43.5rem (696px) = bu grid'in kendi minimum içerik genişliği: 18rem sol + 1.5rem
            (`gap-6`) + 24rem sağ. `@container`in ölçtüğü, kartın `p-4 sm:p-6` dolgusu
            DÜŞÜLMÜŞ içerik kutusudur — dolgu ayrıca telafi EDİLMEZ (sign-off §3.1: eski
            `xl:` viewport eşiği bu 48px'i unutmuştu, 1280–1399px arası taşmaya sebep oluyordu). */}
        <div className="grid grid-cols-1 gap-6 @[43.5rem]:grid-cols-[minmax(18rem,26rem)_minmax(24rem,1fr)]">
          <DocumentAnalysisPreview
            pages={pages}
            fileName={source?.fileName ?? ''}
            onThumbnailExpired={onThumbnailExpired}
            // Lightbox'ın sağ doğrulama paneli — aşağıdaki `AnalysisExtractedFields`e geçilen
            // AYNI beş değer, İKİNCİ kez HESAPLANMADAN (görev talimatı, S26 devamı).
            fields={item.fields}
            derived={derived}
            derivedSourceOk={derivedSourceOk}
            culture={culture}
            droppedCount={item.droppedCount}
          />

          <div className="flex min-w-0 flex-col gap-4">
            {/* İşleyiş bulguları (§20 `DocumentAnalysisItemDto.issues`, D-L/S16-D16) —
                `AnalysisExtractedFields`'in TAM ÜSTÜNDE bir KARDEŞ, kartın İÇİNDE DEĞİL: bir
                uyarı, değerlere güvenilmeden ÖNCE okunmalı, sonra DEĞİL. HER ZAMAN `tone="info"`
                — `severity`ye göre dallanmaz: kalıcı bir `Onayla` engelleyicisi olan
                `DOCUMENT_ANALYSIS_ARTIFACT_MISSING` bile bu uygulamada `info` basılıyor (aşağı),
                bulgu ASLA ondan daha yüksek sesle konuşmaz — `warning` zaten "bu eşleşmeyle
                ilgili bir sorun var" anlamına ayrılmış (kişi uyuşmazlığı uyarısı), burada
                kullanmak o sinyali sulandırırdı. Metin `DOCCHECK_<code>`den çözülür — kod tel
                üstünde ÖNEKSİZ gelir, `DOCCHECK_` istemcide eklenir; §6.1'in dil paketi
                backend'de zaten dokuz kodun HEPSİNİ tr/en/de taşır (`LocalizationCatalog.cs`),
                bu yüzden burada bulgu-özel bir yerel anahtar YOK — yalnız paket henüz
                yüklenmediyse gösterilen tek bir jenerik yer tutucu (`analysis.issue.generic`,
                `error.generic` DEĞİL — bu bir hata değil). `code` KAPALI bir küme değildir,
                `switch` KURULMAZ. */}
            {item.issues.length > 0 ? (
              <Alert tone="info">
                <div className="flex items-start gap-2">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 carbon:hidden" aria-hidden="true" />
                  {item.issues.length === 1 && item.issues[0] ? (
                    <span>
                      {t(`DOCCHECK_${item.issues[0].code}`, {
                        defaultValue: t('analysis.issue.generic'),
                      })}
                    </span>
                  ) : (
                    <ul className="list-disc space-y-0.5 pl-4">
                      {item.issues.map((issue, i) => (
                        <li key={`${issue.code}-${i}`}>
                          {t(`DOCCHECK_${issue.code}`, {
                            defaultValue: t('analysis.issue.generic'),
                          })}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Alert>
            ) : null}
            <AnalysisExtractedFields
              fields={item.fields}
              derived={derived}
              derivedSourceOk={derivedSourceOk}
              culture={culture}
              droppedCount={item.droppedCount}
            />

            {resolved ? (
              <div className="flex items-center gap-1.5 border border-border bg-surface-muted p-3 text-sm">
                {status === 'accepted' ? (
                  <>
                    <Check className="h-4 w-4 shrink-0 text-success" aria-hidden="true" />
                    <span className="font-medium text-fg">
                      {t('analysis.item.status.accepted')}
                    </span>
                  </>
                ) : (
                  <>
                    <Ban className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
                    <span className="font-medium text-muted">{t('analysis.item.rejected')}</span>
                  </>
                )}
              </div>
            ) : !filingSupported ? (
              // #33 B.3 — `vehicle` kalemin kabul hedefi yok (#17'ye kadar): form yerine tek bir
              // bilgi notu; footer'da yalnız `Reddet` kalır (`artifactMissing` ile AYNI `info` tonu).
              <div className="flex flex-col gap-4">
                <Alert tone="info">{t('analysis.review.vehicleRejectOnly')}</Alert>
                {rejectMutation.isError ? (
                  <Alert tone="danger">{resolveApiErrorMessage(rejectMutation.error, t)}</Alert>
                ) : null}
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {artifactMissing ? (
                  <Alert tone="info">
                    {t('DOCUMENT_ANALYSIS_ARTIFACT_MISSING', { defaultValue: t('error.generic') })}
                  </Alert>
                ) : null}

                {needsTargetChoice ? (
                  <Field htmlFor="review-target" label={t('analysis.review.target.label')} required>
                    <Select
                      id="review-target"
                      options={[
                        { value: 'company', label: t('analysis.review.target.company') },
                        { value: 'personnel', label: t('analysis.review.target.personnel') },
                      ]}
                      placeholder={t('analysis.review.target.placeholder')}
                      value={chosenTarget}
                      disabled={artifactMissing}
                      onChange={(e) => {
                        setChosenTarget(e.target.value as AcceptDocumentAnalysisTarget);
                        setDocTypeChoice(null);
                        setTypeRejected(false);
                        setPersonUuid('');
                        setPersonNotFoundError(false);
                      }}
                    />
                  </Field>
                ) : null}

                {/* Kaydın durumu (yüklenemedi / bu hedefte aktif tür yok) seçicinin üstünde. */}
                {targetPending ? null : (
                  <DocumentTypesStatus entity={target} refetchOnMount={false} />
                )}

                {targetPending ? null : isCompanyGroup ? (
                  typeField
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field
                      htmlFor="review-person"
                      label={t('analysis.accept.subjectPicker')}
                      // A11y (S25 güvenlik incelemesi) — `Tooltip`'in `<button>`'ı `label`'ın
                      // İÇİNE değil `labelExtra`ya konur: `<label htmlFor="review-person">`
                      // içindeki bir buton tıklandığında native forwarding altındaki Kişi
                      // seçicisini de AÇARDI (`Field.tsx` dosya üstü notu).
                      labelExtra={
                        mismatch ? (
                          <Tooltip
                            icon={TriangleAlert}
                            label={t(`analysis.person.mismatch.${mismatch}Label`)}
                            content={t(`analysis.person.mismatch.${mismatch}Content`)}
                          />
                        ) : null
                      }
                      required
                      error={
                        personNotFoundError
                          ? t('PERSONNEL_NOT_FOUND', { defaultValue: t('error.generic') })
                          : undefined
                      }
                      // #2 (S25 web kod incelemesi) — davetli eşleşmesi rozetle YETİNMEZ: kişi
                      // neden seçilemediğini (henüz etkinleşmedi) burada da AÇIKÇA yazar. `error`
                      // varken `Field` bunu zaten BASTIRIR (`Field.tsx`), iki bilgi ÇAKIŞMAZ.
                      hint={invitedSubject ? t('analysis.person.invitedHint') : undefined}
                    >
                      {personnelQuery.data &&
                      personnelOptions.length === 0 &&
                      !unregisteredSubject ? (
                        <PersonnelEmptyState loading={false} />
                      ) : (
                        <PersonSelect
                          id="review-person"
                          triggerRef={personTriggerRef}
                          value={personUuid}
                          onChange={setPersonUuid}
                          options={personnelOptions}
                          placeholder={
                            personnelQuery.isPending
                              ? t('common.loading')
                              : t('analysis.accept.subjectPicker')
                          }
                          disabled={personnelQuery.isPending || artifactMissing}
                          invalid={personNotFoundError}
                          ariaDescribedBy={personNotFoundError ? 'review-person-error' : undefined}
                          createOption={
                            unregisteredSubject
                              ? {
                                  label: t('analysis.person.createNew'),
                                  onSelect: () => setCreateOpen(true),
                                }
                              : undefined
                          }
                        />
                      )}
                    </Field>
                    {typeField}
                  </div>
                )}

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field htmlFor="review-valid-from" label={t('documents.field.validFrom')}>
                    <Input
                      id="review-valid-from"
                      type="date"
                      value={validFrom}
                      disabled={artifactMissing}
                      onChange={(e) => setValidFrom(e.target.value)}
                    />
                  </Field>
                  {/* Türün kuralına göre: `NoExpiry`/tür yok → hiç çizilmez. Tarih belgeden
                      hesaplandıysa o ipucu kural ipucunun yerine geçer (zorunluluk yıldızda). */}
                  <ValidUntilInput
                    id="review-valid-until"
                    label={t('documents.field.validUntil')}
                    rule={expiryRule}
                    value={validUntil}
                    disabled={artifactMissing}
                    hint={
                      validity.validUntilInferred && validUntil.length > 0
                        ? t('analysis.accept.validUntilInferredHint')
                        : undefined
                    }
                    onChange={(value) => {
                      validUntilTouchedRef.current = true;
                      setValidUntil(value);
                    }}
                  />
                </div>

                <Field htmlFor="review-note" label={t('documents.field.note')}>
                  <Input
                    id="review-note"
                    value={note}
                    maxLength={500}
                    disabled={artifactMissing}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </Field>

                {rejectMutation.isError ? (
                  <Alert tone="danger">{resolveApiErrorMessage(rejectMutation.error, t)}</Alert>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Yapışkan alt çubuk — `--z-base` (sticky, DropdownMenu/Modal/Tooltip'in üstünde bir
          KATMAN İDDİA ETMEZ; sayfa içeriğidir, bir portal DEĞİLDİR). */}
      <div className="border-t xl:sticky xl:bottom-0 xl:z-[var(--z-base)] border-border bg-surface px-4 py-3 sm:px-6">
        {resolved ? (
          <p className="text-sm text-muted">
            {status === 'accepted' ? t('analysis.accept.success') : t('analysis.item.rejected')}
          </p>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <Tooltip
              label={t('analysis.review.footer.infoLabel')}
              content={t('analysis.review.footer.infoContent')}
            />
            <div className="flex items-center gap-2">
              {/* C11 — Reddet tek tıktır, sebep sorulmaz; ray'deki `geri al` bunu geri alınabilir
                  kılar (§20.4 restore). */}
              <Button
                variant="secondary"
                onClick={() => rejectMutation.mutate()}
                loading={rejectMutation.isPending}
                disabled={acceptMutation.isPending}
              >
                {t('analysis.item.reject')}
              </Button>
              {/* C10 — `Onayla` HER ZAMAN onay diyaloğu açar, mismatch olsun olmasın. #33 B.3 —
                  `vehicle` kalemde hiç RENDER EDİLMEZ (kabul hedefi yok; yalnız `Reddet`). */}
              {filingSupported ? (
                <Button
                  ref={confirmTrigger.ref}
                  variant="primary"
                  onClick={openConfirm}
                  disabled={!canSubmit}
                >
                  {t('analysis.accept.confirm')}
                </Button>
              ) : null}
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          setConfirmOpen(open);
          // Yalnız Vazgeç/Esc/dış tık (karar VERİLMEDEN kapanış) odağı `Onayla`ya geri verir —
          // başarılı bir kabul SONRASI bu KASITLI OLARAK atlanır (D10, üstteki `justDecidedRef` notu).
          if (!open && !justDecidedRef.current) {
            confirmTrigger.restore();
          }
        }}
        title={t('analysis.review.confirm.title')}
        body={
          <div className="flex flex-col gap-3">
            <dl className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-muted">{t('analysis.review.confirm.document')}</dt>
                <dd className="text-right font-medium text-fg">{chosenTypeLabel}</dd>
              </div>
              {!isCompanyGroup ? (
                <div className="flex items-baseline justify-between gap-3">
                  <dt className="text-muted">{t('analysis.review.confirm.person')}</dt>
                  <dd className="text-right font-medium text-fg">{selectedPersonLabel}</dd>
                </div>
              ) : null}
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-muted">{t('analysis.review.confirm.validity')}</dt>
                <dd className="text-right font-medium tnum text-fg">{validitySummary}</dd>
              </div>
            </dl>
            {/* Sign-off §3.7 — kalıcılık uyarısı HER onayda aynen görünür (boilerplate); `warning`
                tonu bu diyalogda "BU eşleşmeyle ilgili bir sorun var" anlamına ayrılır, o yüzden
                kalıcılık `info` tonuna indirgenir. Aksi hâlde bir uyuşmazlık oluştuğunda iki
                sarı blok üst üste yığılır ve asıl istisnai sinyal (yanlış kişi olabilir) her
                onayda tekrar eden metinle AYNI ağırlıkta görünürdü. */}
            <Alert tone="info">{t('analysis.review.confirm.warning')}</Alert>
            {/* C8 §3 — uyuşmazlık algılandığında EK bir uyarı satırı (ekran 3c); onayı ASLA
                ENGELLEMEZ, yalnız anlaşmazlığı ADLANDIRIR. Bu diyalogdaki TEK `warning` — istisnai
                sinyal artık boilerplate'in ARDINDAN görsel olarak KAYBOLMAZ (sign-off §3.7). */}
            {mismatch ? (
              <Alert tone="warning">{t(`analysis.review.confirm.mismatch.${mismatch}`)}</Alert>
            ) : null}
            {copyFailed ? (
              <Alert tone="danger">
                {t('DOCUMENT_ANALYSIS_ARTIFACT_COPY_FAILED', { defaultValue: t('error.generic') })}
              </Alert>
            ) : null}
          </div>
        }
        confirmLabel={t('analysis.accept.confirm')}
        confirmVariant="primary"
        loading={acceptMutation.isPending}
        error={
          acceptMutation.isError && !isTypeFieldRejection(acceptMutation.error)
            ? acceptMutation.error
            : undefined
        }
        errorFallback="analysis.review.confirm.failed"
        onConfirm={() => acceptMutation.mutate()}
      />

      {!isCompanyGroup ? (
        <PersonnelCreateModal
          open={createOpen}
          onOpenChange={(open) => {
            setCreateOpen(open);
            // Yalnız İPTAL/Esc/dış tık (dosya üstü not) — başarılı oluşturma `onCreated` içinde
            // AYNI ref'e ZATEN odaklanır, burada İKİNCİ kez YAPILMAZ.
            if (!open && !justCreatedRef.current) {
              requestAnimationFrame(() => personTriggerRef.current?.focus());
            }
          }}
          companyId={companyId}
          prefill={{ fullName: groupIdentity.fullName, tcKimlikNo: groupIdentity.tcKimlikNo }}
          onCreated={(created) => {
            justCreatedRef.current = true;
            setCreateOpen(false);
            setPersonUuid(created.uuid);
            requestAnimationFrame(() => personTriggerRef.current?.focus());
          }}
        />
      ) : null}
    </div>
  );
}
